import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { IconCheck } from '@tabler/icons-react';

// Apply the fade before paint so a new review never flashes at full opacity
// before its fade-in starts (the Bricks version applied the keyframe
// declaratively). Falls back to useEffect during SSR to avoid the warning.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

interface Review {
  rating: number;
  reviewer: {
    city: string;
    productType: string;
    name?: string;
  };
  excerpt?: string;
}

const STUBBED_REVIEWS: Review[] = [
  {
    rating: 5,
    reviewer: {
      city: 'San Antonio',
      productType: 'sell',
    },
  },
  {
    rating: 5,
    reviewer: {
      city: 'Oklahoma City',
      productType: 'sell',
    },
  },
  {
    rating: 5,
    reviewer: {
      city: 'Phoenix',
      productType: 'sell',
    },
  },
];

const STAR_RATING_URI =
  'https://images.opendoor.com/source/s3/imgdrop-production/2024-11-1732263853253-76957.svg?service=cosmos&preset=square-2048';

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);
  }, []);

  return prefersReducedMotion;
};

export const SocialProofBox = ({ autoplayInterval = 4000 }: { autoplayInterval?: number }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const intervalRef = useRef<NodeJS.Timeout>();
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return undefined;
    }

    intervalRef.current = setInterval(() => {
      setCurrentReviewIndex((prevIndex) =>
        prevIndex === STUBBED_REVIEWS.length - 1 ? 0 : prevIndex + 1,
      );
    }, autoplayInterval);

    return () => clearInterval(intervalRef.current);
  }, [prefersReducedMotion, autoplayInterval]);

  return (
    <CarouselFormat
      reviews={STUBBED_REVIEWS}
      currentIndex={currentReviewIndex}
      interval={autoplayInterval}
    />
  );
};

const FiveStarBanner = ({ rating }: { rating: number }) => (
  // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- composite of star images announced as a single rating
  <Row role="img" aria-label={`${rating} out of 5 stars`} className="gap-0.5">
    {Array.from({ length: rating }).map((_, index) => (
      <Image key={index} alt="" src={STAR_RATING_URI} width={16} height={16} />
    ))}
  </Row>
);

const ReviewNav = () => (
  <Col className="justify-end">
    <Link
      analyticsName="social-proof-carousel-see-reviews"
      aria-label="See reviews"
      href="https://opendoor.com/reviews"
      className="w-19 font-normal text-label-sm !no-underline lg:w-22 lg:text-label-md"
    >
      See reviews
    </Link>
  </Col>
);

const CarouselFormat = ({
  reviews,
  currentIndex,
  interval,
}: {
  reviews: Review[];
  currentIndex: number;
  interval: number;
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const fadeRef = useRef<HTMLElement>(null);

  // Fade each review in then slowly out over the autoplay interval, restarting
  // on every review change. Driven via the Web Animations API because the
  // duration tracks the dynamic `interval` and Atoms primitives accept neither
  // `style` nor dynamically-built Tailwind classes.
  useIsomorphicLayoutEffect(() => {
    const node = fadeRef.current;
    if (prefersReducedMotion || !node || typeof node.animate !== 'function') return undefined;

    const animation = node.animate(
      [
        { opacity: 0, offset: 0, easing: 'ease-in-out' },
        { opacity: 1, offset: 0.25, easing: 'ease-in-out' },
        { opacity: 0, offset: 1 },
      ],
      { duration: interval, iterations: Infinity },
    );

    return () => animation.cancel();
  }, [prefersReducedMotion, interval, currentIndex]);

  if (reviews.length === 0) return null;

  const currentReview = reviews[currentIndex];

  return (
    <Col className="w-full flex-auto justify-between gap-1 rounded-xs bg-od-bg-tertiary/64 p-4 backdrop-blur-xl max-md:min-h-32 md:rounded-3xl lg:w-163 lg:flex-row lg:gap-2.5 lg:p-6">
      <Col ref={fadeRef} className="w-full min-w-0 flex-auto">
        <FiveStarBanner rating={currentReview.rating} />
        <Row className="items-center gap-1">
          {/* Bricks shrank the seller line to $bodyXsmall/$labelXsmall below lg;
              Atoms has no body-xs/label-xs step, so use the smallest scale step
              (text-xs) on mobile and the -sm token from lg up. */}
          <Text as="span" className="text-xs text-od-content-secondary lg:text-body-sm">
            Seller in&nbsp;{currentReview.reviewer.city}
          </Text>
          <Text as="p" className="text-xs text-od-content-accent-brand lg:text-label-sm">
            {currentReview.reviewer?.name || 'Verified Customer'}
          </Text>
          <IconCheck size={20} className="text-od-content-accent-brand" aria-hidden="true" />
        </Row>
        <Row className="min-w-0 justify-between gap-2">
          <Text
            as="span"
            className="min-w-0 truncate text-body-sm text-od-content-primary lg:text-body-md"
          >
            {currentReview?.excerpt || 'A great experience from the beginning...'}
          </Text>
        </Row>
      </Col>
      <ReviewNav />
    </Col>
  );
};
